import { useEffect } from 'react'

import './_bar-chart.scss'
import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    LinearScale,
    Tooltip
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import styled from 'styled-components'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip)

interface Props {
  chartData?: Array<{
    x: string
    y: number
    date?: string
  }>
  options?: Record<string, any>
  datasetOptions?: Record<string, any>
}

const tooltipTimeouts: Array<{
    timeoutKey: string
    timeout: ReturnType<typeof setTimeout>
}> = []

const clearToooltipTimeouts = (timeoutId?: string) => {
    for (let i = 0; i < tooltipTimeouts.length; i++) {
        if (!timeoutId || timeoutId === tooltipTimeouts[i].timeoutKey) {
            clearTimeout(tooltipTimeouts[i].timeout)
        }
    }
}

const formatTooltipDate = (dateInput: unknown) => {
    if (!dateInput) return ''

    const now = new Date()
    const currentYear = now.getFullYear()

    if (typeof dateInput === 'string') {
        const d = new Date(dateInput)
        if (!Number.isNaN(d.getTime())) {
            const sameYear = d.getFullYear() === currentYear
            const fmt = new Intl.DateTimeFormat(undefined, {
                month: 'short',
                day: 'numeric',
                ...(sameYear ? {} : { year: 'numeric' })
            })
            return fmt.format(d)
        }

        const yearStr = String(currentYear)
        return dateInput
            .replace(new RegExp(`[,\\s]+${yearStr}\\b`), '')
            .trim()
    }

    if (dateInput instanceof Date && !Number.isNaN(dateInput.getTime())) {
        const sameYear = dateInput.getFullYear() === currentYear
        const fmt = new Intl.DateTimeFormat(undefined, {
            month: 'short',
            day: 'numeric',
            ...(sameYear ? {} : { year: 'numeric' })
        })
        return fmt.format(dateInput)
    }

    return String(dateInput)
}

const defaultTooltipDataTransform = (x: unknown, y: number) => ({
    x: formatTooltipDate(x),
    y: `$${y}`
})

export const defaultChartOptions = (
    tooltipDataTransform = defaultTooltipDataTransform
) =>
    Object.freeze({
        responsive: true,
        scales: {
            y: {
                align: 'start',
                ticks: {
                    callback: function (value: any) {
                        return value > 0 ? '$' + value : value
                    },
                    autoSkip: true,
                    maxTicksLimit: 3,
                    color: '#9B9B9B',
                    font: {
                        family: 'Montserrat, sans-serif',
                        size: 12,
                        weight: 400
                    }
                }
            },
            x: {
                ticks: {
                    color: '#9B9B9B',
                    font: {
                        family: 'Montserrat, sans-serif',
                        size: 12,
                        weight: 400
                    }
                }
            }
        },
        plugins: {
            tooltip: {
                enabled: false,
                intersect: false,
                mode: 'index',
                position: 'average',
                external: function (context: any) {
                    let tooltipEl = document.getElementById('chartjs-tooltip')
                    let tooltipCircle = document.getElementById('chartjs-tooltip-circle')

                    if (!tooltipEl) {
                        tooltipEl = document.createElement('div')
                        tooltipEl.classList.add('barchart-tooltip-container')
                        tooltipEl.id = 'chartjs-tooltip'

                        const root = document.createElement('div')
                        root.id = 'tooltipRoot'
                        root.className = 'barchart-tooltip-root'

                        tooltipEl.appendChild(root)

                        document.querySelector('.chart-wrapper')!.appendChild(tooltipEl)
                    }

                    if (!tooltipCircle) {
                        tooltipCircle = document.createElement('div')
                        tooltipCircle.classList.add('barchart-tooltip-circle-outer')
                        tooltipCircle.id = 'chartjs-tooltip-circle'

                        const inner = document.createElement('div')
                        inner.className = 'barchart-tooltip-circle-inner'

                        tooltipCircle.appendChild(inner)

                        document.querySelector('.chart-wrapper')!.appendChild(tooltipCircle)
                    }

                    const tooltipModel = context.tooltip
                    if (tooltipModel.opacity === 0) {
                        tooltipEl.style.opacity = '0'
                        tooltipCircle.style.opacity = '0'
                        return
                    }

                    const { x, y } = tooltipDataTransform(
                        tooltipModel.dataPoints[0].raw.date,
                        tooltipModel.dataPoints[0].raw.y
                    )

                    const tooltipRoot = document.getElementById('tooltipRoot')

                    if (tooltipRoot) {
                        tooltipRoot.replaceChildren()

                        const body = document.createElement('div')
                        body.className = 'barchart-tooltip'
                        body.id = 'tooltipBody'

                        const label = document.createElement('div')
                        label.className = 'barchart-tooltip-label'
                        label.textContent = String(x)

                        const sep = document.createElement('div')
                        sep.className = 'barchart-separator'
                        sep.textContent = '|'

                        const value = document.createElement('div')
                        value.className = 'barchart-tooltip-value'
                        value.textContent = String(y)

                        body.appendChild(label)
                        body.appendChild(sep)
                        body.appendChild(value)

                        const arrow = document.createElement('div')
                        arrow.className = 'barchart-tooltip-bubble-arrow'

                        tooltipRoot.appendChild(body)
                        tooltipRoot.appendChild(arrow)
                    }

                    const { caretY, caretX } = tooltipModel

                    const circleTop = caretY - 10
                    const circleLeft = caretX - 9.5

                    tooltipEl.style.position = 'absolute'

                    tooltipCircle.style.opacity = '0'
                    tooltipCircle.style.transform = 'scale(0)'

                    const circleEl = tooltipCircle

                    clearToooltipTimeouts('circleTimeout')
                    tooltipTimeouts.push({
                        timeoutKey: 'circleTimeout',
                        timeout: setTimeout(() => {
                            circleEl.style.top = `${circleTop}px`
                            circleEl.style.left = `${circleLeft}px`
                            circleEl.style.transform = 'scale(1)'
                            circleEl.style.opacity = '1'
                        }, 300)
                    })

                    const { height, width } = tooltipEl.getBoundingClientRect()
                    const wrapper = document.querySelector('.chart-wrapper') as HTMLElement

                    let top = caretY - height - 14
                    if (top < 1) top = caretY + 14

                    let left = caretX - width / 2
                    const maxLeft = wrapper.clientWidth - width - 1
                    left = Math.max(1, Math.min(maxLeft, left))

                    tooltipEl.classList.toggle('arrow-right', left >= maxLeft - 0.5)

                    tooltipEl.style.opacity = '1'
                    tooltipEl.style.top = `${top}px`
                    tooltipEl.style.left = `${left}px`
                }
            }
        }
    })

const BarChart = ({ chartData, options, datasetOptions = {} }: Props) => {
  const safeChartData = chartData ?? []

  const defaultOptions: any = {
    ...defaultChartOptions(),
    ...options,
    responsive: true,
    maintainAspectRatio: false,
    events: ['mousemove', 'mouseout', 'touchstart', 'touchmove'],
    interaction: {
      intersect: true,
      mode: 'index'
    }
  }

const data: any = {
  datasets: [
    {
      data: safeChartData,
      backgroundColor: function (context: any) {
        const chart = context.chart
        const { ctx, chartArea } = chart
        if (!chartArea) return null
        return getGradientBg(ctx, chartArea)
      },
      borderRadius: Number.MAX_VALUE,
      borderSkipped: false,
      barThickness: 9,
      maxBarThickness: 9,
      ...datasetOptions
    }
  ]
}
  const getGradientBg = (ctx: any, chartArea: any) => {
    const gradientBg = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
    gradientBg.addColorStop(0, '#0080FF')
    gradientBg.addColorStop(1, '#334D7E')
    return gradientBg
  }

  useEffect(() => {
    return () => {
      document.getElementById('chartjs-tooltip')?.remove()
      document.getElementById('chartjs-tooltip-circle')?.remove()
    }
  }, [])

  return (
    <BarContainer
      className='chart-wrapper'
      onMouseOut={() => clearToooltipTimeouts()}
    >
      <Bar
        style={{ zIndex: 1 }}
        className='chart'
        data={data}
        options={defaultOptions}
      />
    </BarContainer>
  )
}

export default BarChart

const BarContainer = styled.div`
  height: 200px;
  width: 100%;
  position: relative;
`